import { observer } from 'mobx-react';
import * as React from 'react';
import { findLink } from '../../../LinkyLinky';
import ExecOnlineLogo from '../../../Native/AEP/ExecOnlineLogo';
import { ProgramInstanceKind } from '../../../ProgramStore/Types';
import { Link } from '../../../Resource/Types';
import { AlreadyTranslated, AlreadyTranslatedText, T, TPlainTextKey } from '../../../Translations';
import UniversityLogoOnWhite from '../UniversityLogoOnWhite';
import ProgramFormatLabel from './ProgramFormatLabel';

// The dashboard program cards (active, upcoming, graduated and past) share this
// frame: a header with the school logo and format, the title, a panel with the
// progress and details, the action buttons, and a footer below a line with the team
// and the upcoming sessions.

export const ProgramCard: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  ...rest
}) => (
  <div className="mb-4 rounded border border-gray-200 bg-white p-6 shadow-sm sm:p-8" {...rest}>
    {children}
  </div>
);

interface HeaderProps {
  links: ReadonlyArray<Link>;
  programInstanceKind: ProgramInstanceKind;
  // Shown in place of the Professor-Led / On-Demand label, e.g. for coaching.
  label?: React.ReactNode;
  children?: React.ReactNode;
}

export const ProgramCardHeader: React.FC<HeaderProps> = observer(
  ({ links, programInstanceKind, label, children }) => (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
        {/* Programs without a school partner show the ExecOnline logo. The box is
            hidden if the partner logo fails to load, so the format label is not indented. */}
        <div className="peer flex h-10 items-center empty:hidden [&_img]:max-h-10 [&_img]:max-w-[160px] [&_img]:object-contain [&_img]:p-0">
          {findLink('logo-on-white', links).cata({
            Just: () => <UniversityLogoOnWhite links={links} />,
            Nothing: () => <ExecOnlineLogo size="tiny" />,
          })}
        </div>
        {/* Partner logos are usually heavier at the top, so a label centered on the
            logo looks low. A divider gives the row a clear center line to read against. */}
        <span aria-hidden="true" className="h-6 w-px bg-gray-300 peer-empty:hidden" />
        {label ?? <ProgramFormatLabel programInstanceKind={programInstanceKind} />}
      </div>
      {children}
    </div>
  ),
);

export const ProgramCardTitle: React.FC<{ title: AlreadyTranslatedText }> = ({ title }) => (
  <h3
    className="mb-0 mt-3 text-2xl font-semibold text-gray-900"
    data-test-program-title={title.text}
  >
    <AlreadyTranslated content={title} />
  </h3>
);

// The row under the title: the panel on the left and the action buttons on the
// right. On a narrow screen the buttons wrap below the panel.
export const ProgramCardBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-4">{children}</div>
);

export const ProgramCardPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-w-0 flex-1 basis-80 items-center gap-5">{children}</div>
);

export const ProgramCardActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="ml-auto flex shrink-0 flex-wrap items-center gap-4 empty:hidden">{children}</div>
);

// Under the body: a line, then groups of boxes, such as the team and the upcoming
// sessions. When no group shows, the footer is empty and hides with its line.
export const ProgramCardFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-6 flex flex-wrap gap-x-8 gap-y-5 border-t border-gray-200 pt-6 empty:hidden">
    {children}
  </div>
);

export const ProgramCardFooterGroup: React.FC<{
  label: TPlainTextKey;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div>
    <p className="m-0 mb-3 text-xs font-semibold uppercase tracking-wider text-primary-600">
      <T kind={label} />
    </p>
    {children}
  </div>
);

// A fixed height keeps the team box and the session boxes the same size.
export const programCardBoxClassName =
  'flex h-14 items-center gap-3 rounded border border-logo-200 bg-primary-50 px-3';

export const programCardBoxHoverClassName =
  'cursor-pointer text-left no-underline transition-colors hover:border-primary-300 hover:bg-logo-100';

export const programCardButtonClassName =
  'ml-auto inline-flex items-center gap-3 rounded bg-primary px-6 py-3 text-base font-semibold text-white no-underline shadow-sm transition-colors hover:bg-primary-600 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
